import { GVPartialResult } from "./model";
import { GVResult } from "./model";
import { GVFlightPart } from "./model";
import { GVOffer } from "./model";

/**
 * Formats a duration as a user-friendly string
 * Durations shorter than an hour get shown as "%i minuten",
 * longer durations get shown as "%i:%i" and possibly appended
 * "+%i" for durations longer than 24 hours.
 * @param d The duration in seconds
 * @returns {string} The formatted string
 */
function formatDuration(d) {
  const dys = Math.floor(d / 1440);
  const hrs = Math.floor(d / 60) % 24;
  const min = Math.floor(d % 60);
  const mins = `${min}`.padStart(2, "0");
  if (dys !== 0) return `${hrs}:${mins}+${dys}`;
  else if (hrs === 0) return `${min} minuten`;
  else return `${hrs}:${mins}`;
}

/**
 * Formats a time of day relative to a start date
 * Any time of day after the start date will be shown as
 * hh:mm+d where d represents the number of days after
 * the start date. The start date is considered a strict
 * time, so any time earlier than startDate on the same day
 * is considered to be on the next day. This is to support
 * buggy providers which do not correctly handle this edge
 * case.
 * @param {Date} t The date object to format
 * @param {Date} startDate The start date to refer to
 * @returns {string} The formatted string
 */
function formatTime(t, startDate) {
  const timeFromStart = t - startDate;
  const hrs = t.getHours();
  const min = t.getMinutes();
  const shrs = startDate.getHours();
  const smin = startDate.getMinutes();
  let daysFromStart = Math.floor(timeFromStart / 86400000);
  if (daysFromStart === 0 && (hrs < shrs || (shrs === hrs && min < smin))) {
    daysFromStart++;
  }
  const mins = `${min}`.padStart(2, "0");
  if (daysFromStart > 0) {
    return `${hrs}:${mins}+${daysFromStart}`;
  } else {
    return `${hrs}:${mins}`;
  }
}

export class GVFlightPartView {
  /**
   * Describes a result as formatted for display on the result list
   * @param {GVFlightPart} result
   * @param {String[]} airportNames
   * @param {String[]} airlineNames
   * @param {Date} startDate
   * @param {number} transfer Duration of transfer from previous flight to this one.
   */
  constructor(result, airportNames, airlineNames, startDate, transfer) {
    this.origin = airportNames[result.origin];
    this.originIATA = result.origin;
    this.destination = airportNames[result.destination];
    this.destinationIATA = result.destination;
    this.airline = { name: airlineNames[result.airline], code: result.airline };
    this.duration = formatDuration(result.duration);
    this.departure = formatTime(result.departureDate, startDate);
    this.arrival = formatTime(result.arrivalDate, startDate);
    this.transfer = transfer;
  }
}

export class GVPartialResultView {
  /**
   * Describes a result as formatted for display on the result list
   * @param {GVPartialResult} result
   * @param {String...} airportNames
   * @param {String...} airlineNames
   */
  constructor(result, airportNames, airlineNames) {
    const startDate = result.departureDate;
    this.parts = [];
    this.transfers = [];
    /**
     * @type {GVFlightPart}
     */
    let lp = null;
    for (let p of result.parts) {
      let transfer = null;
      if (lp) {
        transfer = formatDuration((p.departureDate - lp.arrivalDate) / 60000);
        this.transfers.push(transfer);
      }
      this.parts.push(
        new GVFlightPartView(p, airportNames, airlineNames, startDate, transfer)
      );
      lp = p;
    }
    const airlines = new Set(result.parts.map((p) => p.airline));
    if (airlines.size !== 1) {
      this.airline = "Meerdere airlines";
    } else {
      this.airline = airlineNames[result.parts[0].airline];
    }
    this.airlines = [];
    airlines.forEach((v, v2, s) =>
      this.airlines.push({ code: v, name: airportNames[v] })
    );
    this.duration = formatDuration(result.duration);
    this.origin = this.parts[0].origin;
    this.originIATA = this.parts[0].originIATA;
    this.destination = this.parts[this.parts.length - 1].destination;
    this.destinationIATA = this.parts[this.parts.length - 1].destinationIATA;
    this.departure = this.parts[0].departure;
    this.arrival = this.parts[this.parts.length - 1].arrival; //TODO: Use GVPartialResults properties here
    this.stops = this.parts.length - 1;
  }
}

export class GVOfferView {
  /**
   *
   * @param {GVOffer} offer
   * @param {String[]} providerNames
   */
  constructor(offer, providerNames) {
    this.url = offer.url;
    this.text = providerNames[offer.code];
    this.code = offer.code;
    this.unique = offer.unique;
    this.price = offer.price;
  }
}

export class GVLinkView {
  /**
   *
   * @param {GVLink} link
   * @param {String[]} providerNames Lookup table for provider codes
   */
  constructor(link, providerNames) {
    this.url = link.url;
    this.text = providerNames[link.code];
    this.code = link.code;
    //offer.
  }
}

export class GVResultView {
  /**
   * Describes a result as formatted for display on the result list
   * @param {GVResult} result
   * @param {String[]} airportNames  Lookup table for IATA codes
   * @param {String[]} airlineNames  Lookup table for airline codes
   * @param {String[]} providerNames Lookup table for provider codes
   */
  constructor(result, airportNames, airlineNames, providerNames) {
    this.roundtrip = result.roundtrip;
    this.outbound = new GVPartialResultView(
      result.outbound,
      airportNames,
      airlineNames
    );
    if (result.roundtrip) {
      this.inbound = new GVPartialResultView(
        result.inbound,
        airportNames,
        airlineNames
      );
    }
    this.offers = result.offers.map((o) => new GVOfferView(o, providerNames));
    this.uniek = result.unique;
    this.cheapestPrice = result.cheapestPrice;
    this.offerCount = this.offers.length;
    this.singleOffer = this.offerCount === 1;
    this.multiPassenger = result.multiplePersons;
    this.cheapestOfferer = providerNames[result.cheapestOffer.code];
    this.unique = result.unique;
    this.isNonStop =
      Math.max.apply(
        null,
        result.flights.map((f) => f.length)
      ) === 1;
  }
}
